import { Copy, ExternalLink, Mail as MailIcon, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { emailKey, emailPopups, type EmailContext, type EmailItem, type NowContext } from '@tempo/core';
import { store } from '../../lib/device';
import { dayName, time } from '../../lib/format';
import { toast } from '../../lib/toast';
import { Button } from '../ui';

const when = (iso: string | null) => {
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(ms) ? null : `${dayName(ms)} ${time(ms)}`;
};

// Tempo only shows email. Sending happens in School Manager, after the user approves the exact reply in Claude.
const copyPhrase = (phrase: string) =>
  navigator.clipboard.writeText(phrase).then(
    () => toast('Copied – paste it to School Manager in Claude; it shows you the email before sending'),
    () => toast(phrase),
  );

/** The reply School Manager drafted, as it wrote it (edits made in the mail app aren't reflected). */
function RecommendedReply({ text, open = false }: { text: string; open?: boolean }) {
  return (
    <details className="mail-reply" open={open}>
      <summary className="small subtle">Recommended reply</summary>
      <div className="mail-reply-text">{text}</div>
    </details>
  );
}

function Mail({ m, actions = true }: { m: EmailItem; actions?: boolean }) {
  const replyBy = when(m.replyBy);
  return (
    <li className={`mail ${m.priority === 1 ? 'urgent' : ''}`}>
      <div className="mail-head">
        <b>{m.from ?? 'Someone'}</b>
        {m.subject && <span className="subtle"> · {m.subject}</span>}
      </div>
      {m.summary && <div className="mail-summary">{m.summary}</div>}
      <div className="tiny faint">{[replyBy && `reply by ${replyBy}`, !actions && when(m.date), m.accountLabel].filter(Boolean).join(' · ')}</div>
      {actions && m.draft?.text && <RecommendedReply text={m.draft.text} />}
      {actions && (m.url || m.draft) && (
        <div className="row wrap" style={{ gap: 6 }}>
          {m.url && (
            <a className="btn sm" href={m.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={13} /> Open
            </a>
          )}
          {m.draft && (
            <>
              <span className="badge ok">Reply drafted</span>
              <Button size="sm" onClick={() => copyPhrase(m.draft!.sendPhrase)} title="Paste this to School Manager in Claude to review and send">
                <Copy size={13} /> {m.draft.sendPhrase}
              </Button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

/** Email that needs you, from School Manager's inbox checks. Display only. */
export function EmailTool({ n }: { n: NowContext }) {
  return n.email ? <EmailList e={n.email} /> : null;
}

/** Needs you · drafts waiting · worth knowing (collapsed). Also used on the Everything page. */
export function EmailList({ e }: { e: EmailContext }) {
  const checked = when(e.checkedAt);
  return (
    <div className="stack">
      {e.needsYou.length ? (
        <ul className="mail-list">
          {e.needsYou.map((m) => (
            <Mail key={m.id} m={m} />
          ))}
        </ul>
      ) : (
        <p className="subtle">Nothing needs a reply.</p>
      )}
      {e.draftsWaiting.length > 0 && (
        <div className="stack" style={{ gap: 6 }}>
          <div className="tiny faint">Drafts waiting</div>
          {e.draftsWaiting.map((d) => (
            <div key={d.id} className="row wrap mail-draft">
              <span>
                {d.subject ?? '(no subject)'} {d.accountLabel && <span className="tiny faint">· {d.accountLabel}</span>}
              </span>
              <Button size="sm" onClick={() => copyPhrase(d.sendPhrase)}>
                <Copy size={13} /> {d.sendPhrase}
              </Button>
            </div>
          ))}
        </div>
      )}
      {e.worthKnowing.length > 0 && (
        <details className="mail-more">
          <summary className="small subtle">Worth knowing ({e.worthKnowing.length})</summary>
          <ul className="mail-list">
            {e.worthKnowing.map((m) => (
              <Mail key={m.id} m={m} actions={false} />
            ))}
          </ul>
        </details>
      )}
      <p className="tiny faint">
        {checked ? `Inbox checked ${checked}` : 'From School Manager'} · Tempo never sends email: copy a phrase to School Manager in Claude, and it
        sends only after you say yes.
      </p>
    </div>
  );
}

const SINCE_KEY = 'tempo.email.popupsSince';
const DISMISSED_KEY = 'tempo.email.dismissed';

/**
 * Urgent emails that arrived since this device first opened the Assistant and haven't been dismissed here.
 * Dismissing one hides it for good (each email is shown once per time School Manager flags it).
 */
export function useEmailPopups(n: NowContext | undefined) {
  const [since] = useState(() => {
    const saved = store.get<number | null>(SINCE_KEY, null);
    if (saved) return saved;
    const now = Date.now();
    store.set(SINCE_KEY, now);
    return now;
  });
  const [dismissed, setDismissed] = useState(() => new Set(store.get<string[]>(DISMISSED_KEY, [])));
  const popups = useMemo(() => emailPopups(n?.email?.needsYou ?? [], since, dismissed), [n?.email, since, dismissed]);
  const dismiss = (m: EmailItem) =>
    setDismissed((d) => {
      const next = new Set(d).add(emailKey(m));
      store.set(DISMISSED_KEY, [...next].slice(-200));
      return next;
    });
  return { popups, dismiss };
}

/** An urgent email popping up over whatever you're doing: read it, copy the reply's send phrase, dismiss. */
export function EmailPopup({ m, more, onDismiss }: { m: EmailItem; more: number; onDismiss: () => void }) {
  const replyBy = when(m.replyBy);
  return (
    <section className="email-pop" role="status" aria-label="Urgent email">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div className="eyebrow">
          <MailIcon size={13} /> Urgent email{more ? ` · ${more} more` : ''}
        </div>
        <Button size="sm" variant="ghost" icon onClick={onDismiss} aria-label="Dismiss">
          <X size={16} />
        </Button>
      </div>
      <div className="mail-head">
        <b>{m.from ?? 'Someone'}</b>
        {m.subject && <span className="subtle"> · {m.subject}</span>}
      </div>
      {m.summary && <div className="mail-summary">{m.summary}</div>}
      <div className="tiny faint">{[replyBy && `reply by ${replyBy}`, m.accountLabel].filter(Boolean).join(' · ')}</div>
      {m.draft?.text && <RecommendedReply text={m.draft.text} open />}
      <div className="row wrap" style={{ gap: 6 }}>
        {m.draft && (
          <Button size="sm" onClick={() => copyPhrase(m.draft!.sendPhrase)} title="Paste this to School Manager in Claude to review and send">
            <Copy size={13} /> {m.draft.sendPhrase}
          </Button>
        )}
        {m.url && (
          <a className="btn sm" href={m.url} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={13} /> Open
          </a>
        )}
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Got it
        </Button>
      </div>
    </section>
  );
}

/** How many emails need you (for the chip), and whether any is urgent. */
export function emailCount(n: NowContext) {
  const e = n.email;
  return { needs: e?.needsYou.length ?? 0, urgent: e?.needsYou.filter((m) => m.priority === 1).length ?? 0, drafts: e?.draftsWaiting.length ?? 0 };
}
